import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Form, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Subtitle, Paragraph } from "capitalrx-components-2.0/lib/typography";
import { NoResults, SectionPanel, ThemedButton, ThemedCheckbox, ThemedSubmitButton } from "@/App/components";
import { Icon, LoadingGroup, Spinner, useAlert } from "capitalrx-components-2.0/lib/general";

import {
  Pharmacy,
  useListFavoritePharmaciesQuery,
  useListPharmaciesQuery,
  LocationGeolocate,
  useLocationGeolocateQuery,
} from "@/App/api/pharmacy";
import { PharmacyResultCard } from "./PharmacyResultCard";
import { useNavigateClient } from "@/App/hooks";
import { useSearchParams } from "react-router";
import { StackOrShelf } from "@/App/components/StackOrShelf";

const ZIP_REGEX = new RegExp("d{5}");
const TRANSLATION_PATH = "DASHBOARD.recentActivityDashboard.findNearbyPharmacies";
const MAX_RESULTS_COUNT = 3;
const LATITUDE_PARAM = "latitude";
const LONGITUDE_PARAM = "longitude";
const SEARCH_TERM_PARAM = "searchNear";

interface NearbyPharmacyForm {
  readonly location: string;
  readonly useCurrentLocation: boolean;
}

export interface PharmacyWithFavorite extends Pharmacy {
  isFavorite: boolean; // allow setting property to update without re-querying
}

export const PharmacySearchCard: FC = () => {
  const { t: translate } = useTranslation();
  const alert = useAlert();
  const navigate = useNavigateClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [enableFavoritesQuery, setEnableFavoritesQuery] = useState(true);

  const [currentLocation, setCurrentLocation] = useState({
    latitude: decodeURIComponent(searchParams.get(LATITUDE_PARAM) || ""),
    longitude: decodeURIComponent(searchParams.get(LONGITUDE_PARAM) || ""),
  });

  const searchNearbyPharmacyForm = useForm<NearbyPharmacyForm>({
    defaultValues: {
      "location": decodeURIComponent(searchParams.get(SEARCH_TERM_PARAM) || ""),
      "useCurrentLocation": currentLocation.latitude?.length > 0 && currentLocation.longitude?.length > 0,
    },
  });

  const useCurrentLocation = searchNearbyPharmacyForm.watch("useCurrentLocation");
  const searchPlace = searchNearbyPharmacyForm.watch("location");

  const buildSearchParams = () => {
    if (useCurrentLocation) {
        if (currentLocation.latitude.length && currentLocation.longitude.length) {
          const lat = encodeURIComponent(currentLocation.latitude);
          const lon = encodeURIComponent(currentLocation.longitude);
          return `?${LATITUDE_PARAM}=${lat}&${LONGITUDE_PARAM}=${lon}`;
        }
      } else {
        return `?${SEARCH_TERM_PARAM}=${encodeURIComponent(searchPlace)}`;
      }
  };

  const formSubmitIsDisabled = useMemo(() =>
    !(searchPlace || (currentLocation.latitude.length > 0 && currentLocation.longitude.length > 0)),
  [searchPlace, currentLocation]);

  // load search terms from URL
  const [geolocateQueryFilters] = useState(() => {
    if (currentLocation.latitude.length && currentLocation.longitude.length) {
      return currentLocation as Partial<LocationGeolocate>;
    } else {
      if (ZIP_REGEX.test(searchPlace)) {
          return { zip_code: searchPlace } as Partial<LocationGeolocate>;
        } else {
          return { search_address: searchPlace } as Partial<LocationGeolocate>;
        }
    }
  });

  const enableGeolocateQuery = useMemo(() => {
    return !!(geolocateQueryFilters?.latitude && geolocateQueryFilters?.longitude) ||
      geolocateQueryFilters?.zip_code?.length > 0 ||
      geolocateQueryFilters?.search_address?.length > 0;
  }, [geolocateQueryFilters]);

  // to search, set terms in URL (which will cause navigation)
  const onSearchSubmitted = useCallback(
    async () => setSearchParams(buildSearchParams()),
    [currentLocation, searchPlace],
  );

  useEffect(() => {
    if (useCurrentLocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString(),
          });
          searchNearbyPharmacyForm.setValue("location", "");
        },
        (error) => {
          console.error("location request failed", error);
          alert({
            message: translate(`${TRANSLATION_PATH}.unableToGetCurrentLocation`),
            type: "warning",
            style: "toast",
          });
          searchNearbyPharmacyForm.setValue("useCurrentLocation", false);
        },
      );
    } else {
      setCurrentLocation({
        latitude: "",
        longitude: "",
      });
    }
  }, [useCurrentLocation]);

  const { data: locationData, isFetching: isLoadingGeolocation } = useLocationGeolocateQuery(geolocateQueryFilters, enableGeolocateQuery);

  const { data: favoritePharmacies, isFetching: isLoadingFavorites } = useListFavoritePharmaciesQuery(enableFavoritesQuery);

  useEffect(() => setEnableFavoritesQuery(false), [favoritePharmacies]);

  const {
    data: pharmacyData,
    isLoading: isLoadingPharmacyData,
  } = useListPharmaciesQuery({
    latitude: locationData?.latitude,
    longitude: locationData?.longitude,
    results_per_page: MAX_RESULTS_COUNT,
    page_number: 1,
    favorite_pharmacy_provider_ids_display_first: (favoritePharmacies?.results || []).map((pharmacy) => pharmacy.pharmacy.provider_id),
    ignore_custom_rules: true,
  }, (!!favoritePharmacies && !!locationData?.latitude && !!locationData?.longitude));

  const isLoading = useMemo(() => isLoadingGeolocation || isLoadingFavorites || isLoadingPharmacyData,
  [isLoadingGeolocation, isLoadingFavorites, isLoadingPharmacyData]);

  const pharmaciesWithFavorites = useMemo(() => searchNearbyPharmacyForm.formState.isDirty ? [] :
    pharmacyData?.results.map((pharmacy) => {
      return {
        ...pharmacy.pharmacy,
        isFavorite: (favoritePharmacies?.results || []).map((p) => p.pharmacy.provider_id).includes(pharmacy.pharmacy.provider_id),
      };
    }), [pharmacyData, favoritePharmacies, searchNearbyPharmacyForm.formState]);

  return (
    <Stack spacing="small">
      <Subtitle testId="find-nearby-pharmacies-title">
        {translate(`${TRANSLATION_PATH}.title`)}
      </Subtitle>
      <SectionPanel>
        <Stack>
          <Form form={searchNearbyPharmacyForm} onSubmit={onSearchSubmitted}>
            <Stack>
              <Paragraph>{translate(`${TRANSLATION_PATH}.searchLabel`)}</Paragraph>
              <StackOrShelf responsiveAlignment="stretch">
                <Textbox.Field
                  disabled={useCurrentLocation}
                  hideLabel={true}
                  prefix={<Icon name="search" />}
                  name="location"
                  testId="find-nearby-pharmacies-search-textbox"
                  aria-label={translate(`${TRANSLATION_PATH}.search`)}
                  style={{ width: "100%" }}
                />
                <ThemedSubmitButton
                  disabled={formSubmitIsDisabled}
                  appearance="secondary"
                  testId="find-nearby-pharmacies-search-btn"
                >
                  {translate(`${TRANSLATION_PATH}.search`)}
                </ThemedSubmitButton>
              </StackOrShelf>
              <ThemedCheckbox.Field
                  hideLabel
                  label={translate(`${TRANSLATION_PATH}.useMyCurrentLocation`)}
                  name="useCurrentLocation"
                  testId="find-nearby-pharmacies-use-location"
                />
            </Stack>
          </Form>
          <LoadingGroup loading={isLoading}>
            <LoadingGroup.Placeholder fallback={
              <Shelf>
                <Shelf.Spacer />
                <Spinner size="large" />
                <Shelf.Spacer />
              </Shelf>
            }>
              {pharmaciesWithFavorites && !isLoading &&
                <Stack spacing="small">{pharmaciesWithFavorites?.map((pharmacy, idx) => {
                  return <PharmacyResultCard
                    key={`pharmacy-result-card-${idx}`}
                    buildSearchParams={buildSearchParams}
                    pharmacy={pharmacy} />;
                })}
                </Stack>
              }
              {!pharmaciesWithFavorites && !formSubmitIsDisabled && !isLoading &&
                <NoResults dynamicSize message={translate(`${TRANSLATION_PATH}.noResults`)} />}
            </LoadingGroup.Placeholder>
          </LoadingGroup>
          <Stack.Spacer />
        </Stack>
        {!searchNearbyPharmacyForm.formState.isDirty && parseInt(pharmacyData?.result_count) > MAX_RESULTS_COUNT &&
          <ThemedButton
            onClick={() => navigate(`/nearby-pharmacy/compare-pharmacies${buildSearchParams()}`)}
            style={{ marginTop: "2em" }}
            appearance="text">
              {translate(`${TRANSLATION_PATH}.viewAllButton`)}
          </ThemedButton>
        }
      </SectionPanel>
    </Stack>
  );
};
